/**
 * FinancialReport  —  Daily Financial Reporting tab inside ClinicReport
 *
 * Reads vacation and clinic closure data purely to ADVISE the user. Leave never
 * restricts what can be entered or what is submitted.
 *
 * Scheduling / vacation data read:
 *   GET /api/practitioner-vacations/?clinic=<id>
 *       → `practitioner_profile_PractitionerVacation`
 *       → Row fields used: practitioner, date_ranges. `status` and `leave_type` are
 *         NOT row-level fields — they live inside each `date_ranges` entry
 *         ({start_date, end_date, status, leave_type, reason, admin_note}). See
 *         `activeVacationRange` below.
 *       → Only ranges with status 'approved' (or NULL, for legacy pre-migration-0031
 *         rows) count as leave. Pending and denied requests are ignored entirely.
 *
 *   GET /api/clinic-vacations/?clinic=<id>
 *       → `clinic_ClinicVacation`
 *       → A closure covering the selected date shows a notice for every practitioner.
 *       → Fields used: date_ranges. Clinic closures carry no per-range status.
 *
 * Vacation / closure behaviour — ADVISORY ONLY:
 *   - Every field stays editable and is submitted exactly as typed, on leave days and
 *     clinic-closure days alike. Production, hours and costs are never rewritten.
 *   - Real cases depend on this: a hygienist working alone takes x-rays that must be
 *     billed under a dentist who is away, and a re-bill or adjustment of past work is
 *     dated the day it is entered.
 *   - A badge in the practitioner list plus an amber banner tell the user someone is on
 *     leave (Vacation / Sick Leave / Personal Day / Unpaid Leave); a grey banner marks
 *     a clinic closure. Neither disables anything.
 *   - The API accepts production and hours on any date unconditionally. Do NOT
 *     reintroduce a client-side zeroing or blocking rule to compensate: silently
 *     replacing typed numbers with "0" lost real production and is the bug this
 *     advisory-only design exists to prevent.
 *
 * Clinic closure days (`isClinicClosed`):
 *   `requiredErrors` still does not demand a typed Production / Hours Worked value on a
 *   closure day — staff often have only Collection to record — and a blank submits as
 *   "0", matching every other optional money field. Everything downstream is unchanged:
 *   `practitioner_collection` feeds the clinic report summary's `total_collection`,
 *   which is the fixed total the Collection Breakdown report reconciles its
 *   payment-method lines against.
 */
import { useMemo, useState, useEffect } from "react";
import {
  Button,
  Card,
  FormSubmit,
  FormField,
  ListPractitioner,
  CostProductionBreakdownForm,
  OtherProfitTierInputs,
  OtherDeductionTierInputs,
  Dropdown,
  showSuccess,
  showError,
  ScrollBox,
  PractitionerQuickLookModal,
  ConfirmModal,
} from "@common";
import {
  useCreateReport,
  useUpdateReport,
  useLabInvoiceAttachments,
  useInvisalignInvoices,
  usePractitionerVacations,
  useClinicVacations,
} from "@api/hooks";
import { formatValueForForm, toNumber, isValidNum, isValidSignedNum } from "@utils/utils";
import {
  parseOtherProfit,
  parseOtherDeduction,
  resolveOtherDeductionTiers,
  otherDeductionInputFromSaved,
  buildOtherDeductionEntry,
} from "@utils/practitionerFormUtils";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { usePractitionerSelection } from "@hooks/usePractitionerSelection";
import { formatMoney } from "@utils/format";
// Practitioner Lead: hide compensation rows in read-only practitioner popup on this tab
import { FORM_IDS, canManageLabInvoices, canViewLabInvoices, isPractitionerLead } from "@utils";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";
import { LabInvoiceAttachmentPreview } from "./LabInvoiceAttachments";
import DailyAttachmentsPanel from "./DailyAttachmentsPanel";

const PDF_LAB_COST_DISPLAY_STATUSES = new Set(["suggested", "manual_review", "applied"]);

export function flattenSuggestedLabInvoiceBreakdowns(labInvoiceData, clinicId, date) {
  const clinic = String(clinicId || "").toUpperCase();
  const attachments = labInvoiceData?.results || [];
  return attachments.flatMap((attachment) =>
    (attachment.breakdowns || [])
      .filter((breakdown) => {
        const breakdownClinic = String(breakdown.clinic || "").toUpperCase();
        return (
          PDF_LAB_COST_DISPLAY_STATUSES.has(breakdown.status) &&
          breakdown.attachment_reconciliation_status === "matched" &&
          breakdownClinic === clinic &&
          breakdown.assigned_date === date &&
          breakdown.practitioner_id &&
          breakdown.total_amount !== null &&
          breakdown.total_amount !== undefined
        );
      })
      .map((breakdown) => ({
        ...breakdown,
        attachment_id: attachment.id,
        original_filename: attachment.original_filename,
      })),
  );
}

function getAssignedReconciliationMismatchAttachments(
  labInvoiceData,
  clinicId,
  date,
  practitionerId,
) {
  if (!practitionerId) return [];
  const clinic = String(clinicId || "").toUpperCase();
  const pid = String(practitionerId);
  const attachments = labInvoiceData?.results || [];

  return attachments.filter((attachment) => {
    if (attachment.reconciliation_status !== "mismatch") return false;

    const attachmentClinic = String(attachment.clinic || "").toUpperCase();
    const attachmentMatches =
      attachmentClinic === clinic &&
      attachment.assigned_date === date &&
      String(attachment.practitioner_id || "") === pid;

    const breakdownMatches = (attachment.breakdowns || []).some((breakdown) => {
      const breakdownClinic = String(breakdown.clinic || attachment.clinic || "").toUpperCase();
      return (
        breakdownClinic === clinic &&
        breakdown.assigned_date === date &&
        String(breakdown.practitioner_id || attachment.practitioner_id || "") === pid
      );
    });

    return attachmentMatches || breakdownMatches;
  });
}

// Resolves the vacation range covering `date`, or undefined.
//
// `status` and `leave_type` are per-RANGE fields, not row fields: neither
// PractitionerVacationSerializer nor PractitionerVacationListSerializer exposes them at
// the top level (id, practitioner, practitioner_id, profile_id, profile_name,
// profile_practitioner_type, clinic, date_ranges, created_at, updated_at). Reading
// `vac.status` yields undefined, and `undefined == null` is true — which is how every
// row, pending and denied included, used to count as approved leave.
//
// Since migration 0031 each row holds exactly one range, but iterate anyway so a legacy
// un-split multi-range row still resolves correctly.
//
// `status == null` must keep counting as approved: legacy rows predate the field, and
// the backend availability resolver treats null identically to "approved".
function activeVacationRange(vac, date) {
  return (vac.date_ranges ?? []).find(
    (r) =>
      date >= r.start_date && date <= r.end_date && (r.status === "approved" || r.status == null),
  );
}

function amountToCents(value) {
  const normalized = String(value ?? "")
    .replace(/,/g, "")
    .trim();
  if (normalized === "") return null;
  const number = Number(normalized);
  if (!Number.isFinite(number)) return null;
  return Math.round(number * 100);
}

export default function FinancialReport({
  clinicId,
  date,
  onKpisChange,
  clinicData,
  practitionersData,
  role, // ClinicReport passes user.role — required for isPractitionerLead(role) on modal
  hiddenColumns = [],
  canEdit = true,
  initialPractitionerId = "",
  onPractitionerChange = null,
}) {
  // Calculate KPIs and send to parent
  useEffect(() => {
    if (onKpisChange) {
      const kpis = [
        {
          label: "Total Production Reported",
          value: formatMoney(clinicData?.summary?.total_production),
          icon: "🏠",
        },
        {
          label: "Total Collection Reported",
          value: formatMoney(clinicData?.summary?.total_collection),
          icon: "🐷",
        },
      ];
      onKpisChange(kpis);
    }
  }, [clinicData, onKpisChange]);

  const {
    rosterSorted,
    activePid,
    setActivePid,
    selectedPractitioner,
    quickLookOpen,
    activePractitioner,
    openQuickLook,
    setQuickLookOpen,
    setActivePractitioner,
  } = usePractitionerSelection(practitionersData);

  useEffect(() => {
    const target = String(initialPractitionerId || "").trim();
    if (!target || rosterSorted.length === 0) return;
    const matchedPractitioner = rosterSorted.find((practitioner) => {
      const name = practitioner.profile?.name || practitioner.name || "";
      return String(practitioner.id) === target || name.toLowerCase() === target.toLowerCase();
    });
    if (matchedPractitioner && String(activePid) !== String(matchedPractitioner.id)) {
      setActivePid(matchedPractitioner.id);
    }
  }, [initialPractitionerId, rosterSorted, activePid, setActivePid]);

  function selectPractitioner(pid) {
    setActivePid(pid);
    onPractitionerChange?.(pid);
  }

  const { data: vacationsData } = usePractitionerVacations({ clinic: clinicId });

  const onVacationIds = useMemo(() => {
    const vacations = vacationsData?.results ?? [];
    const ids = new Set();
    for (const p of rosterSorted) {
      if (!p.id) continue;
      const hit = vacations.some(
        (vac) => vac.practitioner === p.id && activeVacationRange(vac, date),
      );
      if (hit) ids.add(p.id);
    }
    return ids;
  }, [vacationsData, rosterSorted, date]);

  const leaveTypeByPractitionerId = useMemo(() => {
    const vacations = vacationsData?.results ?? [];
    const map = {};
    for (const p of rosterSorted) {
      if (!p.id) continue;
      for (const vac of vacations) {
        if (vac.practitioner !== p.id) continue;
        const range = activeVacationRange(vac, date);
        if (range) {
          map[p.id] = range.leave_type || "vacation";
          break;
        }
      }
    }
    return map;
  }, [vacationsData, rosterSorted, date]);

  const { data: clinicVacationsData } = useClinicVacations({ clinic: clinicId });
  const isClinicClosed = useMemo(() => {
    const closures = clinicVacationsData?.results ?? [];
    return closures.some((cv) =>
      (cv.date_ranges ?? []).some((r) => date >= r.start_date && date <= r.end_date),
    );
  }, [clinicVacationsData, date]);

  const isSelectedOnVacation = selectedPractitioner
    ? onVacationIds.has(selectedPractitioner.id)
    : false;

  const selectedLeaveType = useMemo(() => {
    if (!selectedPractitioner?.id) return null;
    const vacations = vacationsData?.results ?? [];
    for (const vac of vacations) {
      if (vac.practitioner !== selectedPractitioner.id) continue;
      const range = activeVacationRange(vac, date);
      if (range) return range.leave_type || "vacation";
    }
    return null;
  }, [vacationsData, selectedPractitioner, date]);

  const createReport = useCreateReport();
  const updateReport = useUpdateReport();
  const { data: labInvoiceData } = useLabInvoiceAttachments(
    { clinic: clinicId, date },
    { enablePolling: true },
  );
  const suggestedLabInvoiceBreakdowns = useMemo(
    () => flattenSuggestedLabInvoiceBreakdowns(labInvoiceData, clinicId, date),
    [labInvoiceData, clinicId, date],
  );

  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(
    FORM_IDS.FINANCIAL_REPORT_PERFORMANCE,
  );
  const reportedIds = useMemo(() => {
    if (!clinicData?.reports || !Array.isArray(clinicData.reports)) return [];

    return rosterSorted
      .filter((practitioner) => {
        return clinicData.reports.some(
          (report) =>
            (report.practitioner_id === practitioner.id ||
              report.practitioner === practitioner.id) &&
            report.practitioner_type?.toLowerCase() ===
              practitioner.practitioner_type?.toLowerCase(),
        );
      })
      .map((p) => p.id);
  }, [clinicData, rosterSorted]);

  const activeReport = useMemo(() => {
    if (!selectedPractitioner) {
      return null;
    }

    if (clinicData?.reports && Array.isArray(clinicData.reports)) {
      const match = clinicData.reports.find(
        (report) =>
          (report.practitioner_id === selectedPractitioner.id ||
            report.practitioner === selectedPractitioner.id) &&
          report.practitioner_type?.toUpperCase() ===
            selectedPractitioner.practitioner_type?.toUpperCase(),
      );

      if (match) {
        return {
          ...selectedPractitioner,
          ...match,
          total_production: match.production?.total,
          total_collection: match.collection?.total,
          total_hours: match.hours_worked?.total,
          total_lab_costs: match.lab_costs?.total,
          total_material_costs: match.material_costs?.total,
          total_invisalign_costs: match.invisalign_costs?.total,
        };
      }
    }

    return {
      ...selectedPractitioner,
      total_production: null,
      total_collection: null,
      total_hours: null,
      total_lab_costs: null,
      total_material_costs: null,
      total_invisalign_costs: null,
    };
  }, [selectedPractitioner, clinicData]);

  const [vals, setVals] = useState({
    production: "",
    collection: "",
    lab: "",
    material: "",
    invisalign: "",
    anesthesia_fee: "",
    anesthesiologist_fee: "",
    facility_fee: "",
    anesthesiologist_production: "",
    facility_production: "",
    botox: "",
    production_adjustment: "",
    collection_adjustment: "",
    hours: "",
  });

  const emptyBreakdowns = () => ({
    production: [],
    collection: [],
    lab: [],
    material: [],
    invisalign: [],
    anesthesia_fee: [],
    anesthesiologist_fee: [],
    facility_fee: [],
    anesthesiologist_production: [],
    facility_production: [],
    botox: [],
    production_adjustment: [],
    collection_adjustment: [],
    hours: [],
  });
  const [breakdowns, setBreakdowns] = useState(emptyBreakdowns());

  function computeBreakdownTotal(entries) {
    // Single-entry: preserve the raw string so mid-type decimals (e.g. "25.")
    // and partial negatives (e.g. "-", "-.") aren't stripped while typing.
    if (entries.length === 1) {
      const raw = entries[0];
      if (raw === "" || raw == null) return "";
      const s = String(raw).trim();
      if (s === "-" || s === "-." || s === "." || s === "-0") return s;
      const n = parseFloat(s);
      return Number.isNaN(n) ? "" : s;
    }
    const nums = entries.map((e) => parseFloat(e)).filter((n) => !Number.isNaN(n));
    if (nums.length === 0) return "";
    const sum = nums.reduce((s, n) => s + n, 0);
    return String(parseFloat(sum.toFixed(2)));
  }

  function handleBreakdownChange(field, entries) {
    setBreakdowns((prev) => ({ ...prev, [field]: entries }));
    setVals((prev) => ({ ...prev, [field]: computeBreakdownTotal(entries) }));
  }

  // Other-profit tiers come from the selected practitioner's profile.
  const otherProfitTiers = useMemo(() => {
    const parsed = parseOtherProfit(selectedPractitioner?.other_profit);
    return parsed.enabled ? parsed.rows : [];
  }, [selectedPractitioner]);
  const hasOtherProfitTiers = otherProfitTiers.length > 0;

  // Per-tier input_value strings, index-aligned with `otherProfitTiers`.
  const [otherProfitInputs, setOtherProfitInputs] = useState([]);

  // Other-deduction tiers come from the selected practitioner's profile, but a
  // month that already has entries keeps the mode and rate it was saved with —
  // editing the profile must not reinterpret or re-price a month on the books.
  const profileOtherDeductionTiers = useMemo(() => {
    const parsed = parseOtherDeduction(selectedPractitioner?.other_deduction);
    return parsed.enabled ? parsed.rows : [];
  }, [selectedPractitioner]);
  const otherDeductionTiers = useMemo(
    () => resolveOtherDeductionTiers(profileOtherDeductionTiers, activeReport?.other_deduction),
    [profileOtherDeductionTiers, activeReport],
  );
  const hasOtherDeductionTiers = otherDeductionTiers.length > 0;

  // Per-tier entry strings (hours or dollars, per tier mode), index-aligned
  // with `otherDeductionTiers`.
  const [otherDeductionInputs, setOtherDeductionInputs] = useState([]);

  // Per-day flag is configured on the practitioner profile. When enabled,
  // surface a Half Day (0.5) / Full Day (1) dropdown on the form.
  const isPerDayEnabled = !!selectedPractitioner?.per_day;
  const PER_DAY_OPTIONS = [
    { label: "Half Day", value: "0.5" },
    { label: "Full Day", value: "1" },
  ];
  const [perDayNumber, setPerDayNumber] = useState("");

  const [isEditing, setIsEditing] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [openLabInvoiceId, setOpenLabInvoiceId] = useState(null);

  const isUpdate = useMemo(
    () => activeReport?.id && clinicData?.reports?.some((r) => r.id === activeReport.id),
    [activeReport, clinicData?.reports],
  );

  // Seed form when activeReport changes — reads ?.total for vals and ?.breakdown for breakdown slots
  useEffect(() => {
    if (activeReport) {
      setVals({
        production: formatValueForForm(activeReport.total_production),
        collection: formatValueForForm(activeReport.total_collection),
        lab: formatValueForForm(activeReport.total_lab_costs),
        material: formatValueForForm(activeReport.total_material_costs),
        invisalign: formatValueForForm(activeReport.total_invisalign_costs),
        anesthesia_fee: formatValueForForm(activeReport.anesthesia_fee?.total),
        anesthesiologist_fee: formatValueForForm(activeReport.anesthesiologist_fee?.total),
        facility_fee: formatValueForForm(activeReport.facility_fee?.total),
        anesthesiologist_production: formatValueForForm(
          activeReport.anesthesiologist_production?.total,
        ),
        facility_production: formatValueForForm(activeReport.facility_production?.total),
        botox: formatValueForForm(activeReport.botox?.total),
        production_adjustment: formatValueForForm(activeReport.production_adjustment?.total),
        collection_adjustment: formatValueForForm(activeReport.collection_adjustment?.total),
        hours: formatValueForForm(activeReport.total_hours),
      });

      const toStringArr = (arr) => (Array.isArray(arr) && arr.length > 0 ? arr.map(String) : []);
      setBreakdowns({
        production: toStringArr(activeReport.production?.breakdown),
        collection: toStringArr(activeReport.collection?.breakdown),
        lab: toStringArr(activeReport.lab_costs?.breakdown),
        material: toStringArr(activeReport.material_costs?.breakdown),
        invisalign: toStringArr(activeReport.invisalign_costs?.breakdown),
        anesthesia_fee: toStringArr(activeReport.anesthesia_fee?.breakdown),
        anesthesiologist_fee: toStringArr(activeReport.anesthesiologist_fee?.breakdown),
        facility_fee: toStringArr(activeReport.facility_fee?.breakdown),
        anesthesiologist_production: toStringArr(
          activeReport.anesthesiologist_production?.breakdown,
        ),
        facility_production: toStringArr(activeReport.facility_production?.breakdown),
        botox: toStringArr(activeReport.botox?.breakdown),
        production_adjustment: toStringArr(activeReport.production_adjustment?.breakdown),
        collection_adjustment: toStringArr(activeReport.collection_adjustment?.breakdown),
        hours: toStringArr(activeReport.hours_worked?.breakdown),
      });

      // Seed per-tier input_values from the saved report, matching by label.
      const savedByLabel = new Map();
      const savedOther = Array.isArray(activeReport.other_profit) ? activeReport.other_profit : [];
      savedOther.forEach((entry) => {
        if (entry && entry.label) savedByLabel.set(String(entry.label), entry);
      });
      setOtherProfitInputs(
        otherProfitTiers.map((t) => {
          const match = savedByLabel.get(String(t.label));
          const v =
            match && match.input_value !== undefined && match.input_value !== null
              ? match.input_value
              : "";
          return v === "" ? "" : formatValueForForm(v);
        }),
      );

      const savedByLabelDed = new Map();
      const savedDed = Array.isArray(activeReport.other_deduction)
        ? activeReport.other_deduction
        : [];
      savedDed.forEach((entry) => {
        if (entry && entry.label) savedByLabelDed.set(String(entry.label), entry);
      });
      setOtherDeductionInputs(
        otherDeductionTiers.map((t) => {
          const v = otherDeductionInputFromSaved(t, savedByLabelDed.get(String(t.label)));
          return v === "" ? "" : formatValueForForm(v);
        }),
      );

      // Seed per-day number from existing report (if any).
      if (
        activeReport.per_day_number !== undefined &&
        activeReport.per_day_number !== null &&
        activeReport.per_day_number !== ""
      ) {
        const num = Number(activeReport.per_day_number);
        if (num === 0.5) setPerDayNumber("0.5");
        else if (num === 1) setPerDayNumber("1");
        else setPerDayNumber("");
      } else {
        setPerDayNumber("");
      }

      setHasSubmitted(false);
    }
  }, [activeReport, otherProfitTiers, otherDeductionTiers]);

  useEffect(() => {
    setIsEditing(!isUpdate);
  }, [isUpdate]);

  useEffect(() => {
    setOpenLabInvoiceId(null);
  }, [clinicId, date]);

  const labInvoiceSuggestions = useMemo(() => {
    if (!selectedPractitioner?.id) return [];
    return suggestedLabInvoiceBreakdowns.filter((breakdown) => {
      return (
        breakdown.practitioner_id &&
        Number(breakdown.practitioner_id) === Number(selectedPractitioner.id)
      );
    });
  }, [suggestedLabInvoiceBreakdowns, selectedPractitioner?.id]);

  const suggestionPdfs = useMemo(() => {
    const byAttachment = new Map();
    labInvoiceSuggestions.forEach((breakdown) => {
      const existing = byAttachment.get(breakdown.attachment_id) || {
        attachmentId: breakdown.attachment_id,
        filename: breakdown.original_filename,
        lineCount: 0,
        amount: 0,
      };
      existing.lineCount += 1;
      existing.amount += Number(breakdown.total_amount || 0);
      byAttachment.set(breakdown.attachment_id, existing);
    });
    return [...byAttachment.values()];
  }, [labInvoiceSuggestions]);

  // Same query (and cache) as the Invisalign Invoices tab above the form. Only
  // invoices read cleanly and matched to this practitioner count; credit notes
  // are negative and reduce the total.
  const { data: invisalignData } = useInvisalignInvoices({ clinic: clinicId, date });
  const invisalignSuggestionText = useMemo(() => {
    if (!selectedPractitioner?.id) return "";
    const invoices = (invisalignData?.results || []).filter(
      (invoice) =>
        invoice.practitioner &&
        Number(invoice.practitioner) === Number(selectedPractitioner.id) &&
        invoice.total_amount !== null &&
        !invoice.review_reason,
    );
    if (invoices.length === 0) return "";
    const totalCents = invoices.reduce(
      (sum, invoice) => sum + Math.round(Number(invoice.total_amount) * 100),
      0,
    );
    const count = `${invoices.length} invoice${invoices.length === 1 ? "" : "s"}`;
    return `Suggested: ${formatMoney(totalCents / 100)} (${count})`;
  }, [invisalignData?.results, selectedPractitioner?.id]);

  const pdfSuggestedLabCost = useMemo(() => {
    return labInvoiceSuggestions.reduce(
      (total, breakdown) => total + Number(breakdown.total_amount || 0),
      0,
    );
  }, [labInvoiceSuggestions]);

  const pdfSuggestedLabCostComment = useMemo(() => {
    if (labInvoiceSuggestions.length === 0) return "";
    const pdfCount = new Set(labInvoiceSuggestions.map((breakdown) => breakdown.attachment_id))
      .size;
    const lineLabel = `${labInvoiceSuggestions.length} lab invoice line${
      labInvoiceSuggestions.length === 1 ? "" : "s"
    }`;
    const pdfLabel = pdfCount > 1 ? ` across ${pdfCount} PDFs` : "";
    return `From ${lineLabel}${pdfLabel}.`;
  }, [labInvoiceSuggestions]);

  const reconciliationMismatchPdfs = useMemo(
    () =>
      getAssignedReconciliationMismatchAttachments(
        labInvoiceData,
        clinicId,
        date,
        selectedPractitioner?.id,
      ),
    [labInvoiceData, clinicId, date, selectedPractitioner?.id],
  );
  const hasReconciliationMismatchPdf = reconciliationMismatchPdfs.length > 0;

  const labCostMatchesPdfSuggestion = useMemo(() => {
    const labCostCents = amountToCents(vals.lab || "0");
    const pdfSuggestedCents = amountToCents(pdfSuggestedLabCost.toFixed(2));
    return labCostCents !== null && labCostCents === pdfSuggestedCents;
  }, [pdfSuggestedLabCost, vals.lab]);

  const labCostMismatchMessage = hasReconciliationMismatchPdf
    ? `Fix PDF total mismatch before submitting (${reconciliationMismatchPdfs
        .map((attachment) => attachment.original_filename)
        .filter(Boolean)
        .slice(0, 3)
        .join(", ")}).`
    : labCostMatchesPdfSuggestion
      ? ""
      : labInvoiceSuggestions.length > 0
        ? `Lab Costs does not match PDF Suggested Lab Costs (${formatMoney(pdfSuggestedLabCost)}).`
        : "No lab invoice PDF for this day — upload the PDF or set Lab Costs to 0.";

  const requiredErrors = useMemo(() => {
    const errors = {};
    // On a clinic closure day Production and Hours Worked stay editable but are not
    // required — staff commonly have only Collection to record. A blank one submits as
    // "0" (see buildPayload); anything typed is submitted as typed. Collection stays
    // required either way, and the "Numbers only" checks still run on whatever value is
    // present, so nothing else about validation changes.
    if (vals.production === "") {
      if (!isClinicClosed) errors.production = "Required";
    } else if (!isValidSignedNum(vals.production)) errors.production = "Numbers only";
    if (vals.collection === "") errors.collection = "Required";
    else if (!isValidSignedNum(vals.collection)) errors.collection = "Numbers only";
    if (!isValidSignedNum(vals.lab)) errors.lab = "Numbers only";
    if (!isValidSignedNum(vals.material)) errors.material = "Numbers only";
    if (!isValidSignedNum(vals.invisalign)) errors.invisalign = "Numbers only";
    if (!isValidSignedNum(vals.anesthesiologist_production))
      errors.anesthesiologist_production = "Numbers only";
    if (!isValidSignedNum(vals.facility_production)) errors.facility_production = "Numbers only";
    if (vals.hours === "") {
      if (!isClinicClosed) errors.hours = "Required";
    } else if (!isValidSignedNum(vals.hours)) errors.hours = "Numbers only";
    return errors;
  }, [vals, isClinicClosed]);

  // Validate each other-profit tier input_value (optional, numeric).
  // Negative values are allowed so corrections / clawbacks can be entered.
  const otherProfitErrors = useMemo(
    () =>
      otherProfitTiers.map((_, idx) => {
        const raw = otherProfitInputs[idx];
        if (raw === undefined || raw === null || raw === "") return "";
        return isValidSignedNum(raw) ? "" : "Numbers only";
      }),
    [otherProfitTiers, otherProfitInputs],
  );
  const otherProfitHasError = otherProfitErrors.some((e) => e !== "");

  // Validate each other-deduction tier input_value (optional, numeric, >=0).
  const otherDeductionErrors = useMemo(
    () =>
      otherDeductionTiers.map((_, idx) => {
        const raw = otherDeductionInputs[idx];
        if (raw === undefined || raw === null || raw === "") return "";
        return isValidNum(raw) ? "" : "Numbers only";
      }),
    [otherDeductionTiers, otherDeductionInputs],
  );
  const otherDeductionHasError = otherDeductionErrors.some((e) => e !== "");

  const canSubmit =
    Object.keys(requiredErrors).length === 0 &&
    !otherProfitHasError &&
    !otherDeductionHasError &&
    !hasReconciliationMismatchPdf;

  const [showNegativeConfirm, setShowNegativeConfirm] = useState(false);
  const [showLabCostMismatchConfirm, setShowLabCostMismatchConfirm] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  const NEGATIVE_FIELD_LABELS = {
    production: "Production",
    collection: "Collection",
    lab: "Lab Costs",
    material: "Material Costs",
    invisalign: "Invisalign Costs",
    anesthesia_fee: "Anesthesia Fee",
    anesthesiologist_fee: "Anesthesiologist Fee",
    facility_fee: "Facility Fee",
    anesthesiologist_production: "Anesthesiologist Production",
    facility_production: "Facility Production",
    botox: "Botox",
    production_adjustment: "Production Adjustment",
    collection_adjustment: "Collection Adjustment",
    hours: "Hours Worked",
  };

  function getNegativeFields(values) {
    return Object.entries(values)
      .filter(([, v]) => v !== "" && v !== null && v !== undefined && Number(v) < 0)
      .map(([k]) => NEGATIVE_FIELD_LABELS[k] || k);
  }

  function handleConfirmNegative() {
    setShowNegativeConfirm(false);
    if (pendingPayload) {
      submitPayload(pendingPayload);
      setPendingPayload(null);
    }
  }

  // Resets form to last saved state (Cancel button) — same seeding logic as the useEffect above
  function resetFormToOriginal() {
    if (!activeReport) return;

    setVals({
      production: formatValueForForm(activeReport.total_production),
      collection: formatValueForForm(activeReport.total_collection),
      lab: formatValueForForm(activeReport.total_lab_costs),
      material: formatValueForForm(activeReport.total_material_costs),
      invisalign: formatValueForForm(activeReport.total_invisalign_costs),
      anesthesia_fee: formatValueForForm(activeReport.anesthesia_fee?.total),
      anesthesiologist_fee: formatValueForForm(activeReport.anesthesiologist_fee?.total),
      facility_fee: formatValueForForm(activeReport.facility_fee?.total),
      anesthesiologist_production: formatValueForForm(
        activeReport.anesthesiologist_production?.total,
      ),
      facility_production: formatValueForForm(activeReport.facility_production?.total),
      botox: formatValueForForm(activeReport.botox?.total),
      production_adjustment: formatValueForForm(activeReport.production_adjustment?.total),
      collection_adjustment: formatValueForForm(activeReport.collection_adjustment?.total),
      hours: formatValueForForm(activeReport.total_hours),
    });

    const toStringArr = (arr) => (Array.isArray(arr) && arr.length > 0 ? arr.map(String) : []);
    setBreakdowns({
      production: toStringArr(activeReport.production?.breakdown),
      collection: toStringArr(activeReport.collection?.breakdown),
      lab: toStringArr(activeReport.lab_costs?.breakdown),
      material: toStringArr(activeReport.material_costs?.breakdown),
      invisalign: toStringArr(activeReport.invisalign_costs?.breakdown),
      anesthesia_fee: toStringArr(activeReport.anesthesia_fee?.breakdown),
      anesthesiologist_fee: toStringArr(activeReport.anesthesiologist_fee?.breakdown),
      facility_fee: toStringArr(activeReport.facility_fee?.breakdown),
      anesthesiologist_production: toStringArr(activeReport.anesthesiologist_production?.breakdown),
      facility_production: toStringArr(activeReport.facility_production?.breakdown),
      botox: toStringArr(activeReport.botox?.breakdown),
      production_adjustment: toStringArr(activeReport.production_adjustment?.breakdown),
      collection_adjustment: toStringArr(activeReport.collection_adjustment?.breakdown),
      hours: toStringArr(activeReport.hours_worked?.breakdown),
    });

    const savedByLabel = new Map();
    const savedOther = Array.isArray(activeReport.other_profit) ? activeReport.other_profit : [];
    savedOther.forEach((entry) => {
      if (entry && entry.label) savedByLabel.set(String(entry.label), entry);
    });
    setOtherProfitInputs(
      otherProfitTiers.map((t) => {
        const match = savedByLabel.get(String(t.label));
        const v =
          match && match.input_value !== undefined && match.input_value !== null
            ? match.input_value
            : "";
        return v === "" ? "" : formatValueForForm(v);
      }),
    );

    const savedByLabelDed = new Map();
    const savedDed = Array.isArray(activeReport.other_deduction)
      ? activeReport.other_deduction
      : [];
    savedDed.forEach((entry) => {
      if (entry && entry.label) savedByLabelDed.set(String(entry.label), entry);
    });
    setOtherDeductionInputs(
      otherDeductionTiers.map((t) => {
        const v = otherDeductionInputFromSaved(t, savedByLabelDed.get(String(t.label)));
        return v === "" ? "" : formatValueForForm(v);
      }),
    );

    if (
      activeReport.per_day_number !== undefined &&
      activeReport.per_day_number !== null &&
      activeReport.per_day_number !== ""
    ) {
      const num = Number(activeReport.per_day_number);
      if (num === 0.5) setPerDayNumber("0.5");
      else if (num === 1) setPerDayNumber("1");
      else setPerDayNumber("");
    } else {
      setPerDayNumber("");
    }
  }

  function buildPayload({ labInvoiceBreakdownIds = [] } = {}) {
    // cleanBreakdown maps each slot to a number, preserving position so slot indices survive the round-trip.
    // Empty slots become 0 (not filtered) so that DCP/DKJ values don't collapse to position 0 on reload.
    const cleanBreakdown = (entries) =>
      entries.map((e) => (e !== "" && e !== null ? parseFloat(e) || 0 : 0));

    const payload = {
      date: date,
      practitioner: isUpdate
        ? activeReport.practitioner_id || selectedPractitioner.id
        : selectedPractitioner.id,
      practitioner_type: isUpdate
        ? activeReport.practitioner_type?.toLowerCase()
        : selectedPractitioner.practitioner_type?.toLowerCase(),
      practitioner_production: vals.production || "0",
      practitioner_collection: vals.collection,
      lab_costs: vals.lab || "0",
      material_costs: vals.material || "0",
      invisalign_costs: vals.invisalign || "0",
      anesthesia_fee: vals.anesthesia_fee || "0",
      anesthesiologist_fee: vals.anesthesiologist_fee || "0",
      facility_fee: vals.facility_fee || "0",
      anesthesiologist_production:
        vals.anesthesiologist_production === "" ? null : vals.anesthesiologist_production,
      facility_production: vals.facility_production === "" ? null : vals.facility_production,
      botox: vals.botox || "0",
      production_adjustment: vals.production_adjustment || "0",
      collection_adjustment: vals.collection_adjustment || "0",
      hours_worked: vals.hours || "0",
      practitioner_production_breakdown: cleanBreakdown(breakdowns.production),
      practitioner_collection_breakdown: cleanBreakdown(breakdowns.collection),
      lab_costs_breakdown: cleanBreakdown(breakdowns.lab),
      material_costs_breakdown: cleanBreakdown(breakdowns.material),
      invisalign_costs_breakdown: cleanBreakdown(breakdowns.invisalign),
      anesthesia_fee_breakdown: cleanBreakdown(breakdowns.anesthesia_fee),
      anesthesiologist_fee_breakdown: cleanBreakdown(breakdowns.anesthesiologist_fee),
      facility_fee_breakdown: cleanBreakdown(breakdowns.facility_fee),
      anesthesiologist_production_breakdown: cleanBreakdown(breakdowns.anesthesiologist_production),
      facility_production_breakdown: cleanBreakdown(breakdowns.facility_production),
      botox_breakdown: cleanBreakdown(breakdowns.botox),
      production_adjustment_breakdown: cleanBreakdown(breakdowns.production_adjustment),
      collection_adjustment_breakdown: cleanBreakdown(breakdowns.collection_adjustment),
      hours_worked_breakdown: cleanBreakdown(breakdowns.hours),
      other_profit: otherProfitTiers.map((t, idx) => {
        const raw = otherProfitInputs[idx];
        const inputValue = raw === "" || raw === undefined || raw === null ? 0 : toNumber(raw);
        const percent = Number(t.percent) || 0;
        const profit = Math.round(((inputValue * percent) / 100) * 100) / 100;
        return {
          label: t.label,
          percent,
          input_value: inputValue,
          profit,
        };
      }),
      other_deduction: otherDeductionTiers.map((t, idx) =>
        buildOtherDeductionEntry(t, otherDeductionInputs[idx]),
      ),
      per_day_number:
        isPerDayEnabled && (perDayNumber === "0.5" || perDayNumber === "1")
          ? toNumber(perDayNumber)
          : null,
      per_day_value:
        isPerDayEnabled &&
        selectedPractitioner?.per_day_value !== undefined &&
        selectedPractitioner?.per_day_value !== null &&
        selectedPractitioner?.per_day_value !== ""
          ? toNumber(selectedPractitioner.per_day_value)
          : null,
      category: "performance",
    };
    if (labInvoiceBreakdownIds.length > 0) {
      payload.lab_invoice_breakdown_ids = labInvoiceBreakdownIds;
    }
    return payload;
  }

  function submitOrConfirmNegative(payload) {
    const negativeFields = getNegativeFields(vals);

    if (negativeFields.length > 0) {
      setPendingPayload(payload);
      setShowNegativeConfirm(true);
      return;
    }

    submitPayload(payload);
  }

  function submitPayload(payload) {
    const mutation = isUpdate ? updateReport : createReport;
    const mutationParams = isUpdate
      ? { clinicId: clinicId, id: activeReport.id, data: payload }
      : { clinicId: clinicId, data: payload };

    mutation.mutate(mutationParams, {
      onSuccess: () => {
        recordSubmitSuccess();
        showSuccess(`Report ${isUpdate ? "updated" : "created"} successfully!`);
        setIsEditing(false);
        setHasSubmitted(false);
      },
      onError: (error) => {
        showError(getApiErrorMessage(error));
        console.error(`${isUpdate ? "Update" : "Create"} error:`, error);
      },
    });
  }

  function onSubmit(e) {
    e.preventDefault();
    setHasSubmitted(true);
    if (!canSubmit) return;

    const suggestionIds = labInvoiceSuggestions.map((breakdown) => breakdown.id);
    const payload = buildPayload({
      labInvoiceBreakdownIds: suggestionIds,
    });

    if (!labCostMatchesPdfSuggestion) {
      setPendingPayload(payload);
      setShowLabCostMismatchConfirm(true);
      return;
    }

    submitOrConfirmNegative(payload);
  }

  function handleConfirmLabCostMismatch() {
    setShowLabCostMismatchConfirm(false);
    if (pendingPayload) {
      const payload = pendingPayload;
      setPendingPayload(null);
      submitOrConfirmNegative(payload);
    }
  }

  // "Did Not Work" shortcut: clears all fields and submits a zero-filled report
  // so the practitioner is marked as reported for the day with no production.
  function handleDidNotWork() {
    if (!selectedPractitioner) return;

    const zeroVals = {
      production: "0",
      collection: "0",
      lab: "",
      material: "",
      invisalign: "",
      anesthesia_fee: "",
      anesthesiologist_fee: "",
      facility_fee: "",
      anesthesiologist_production: "",
      facility_production: "",
      botox: "",
      production_adjustment: "",
      collection_adjustment: "",
      hours: "0",
    };
    setVals(zeroVals);
    setBreakdowns(emptyBreakdowns());
    setOtherProfitInputs(otherProfitTiers.map(() => ""));
    setOtherDeductionInputs(otherDeductionTiers.map(() => ""));
    setPerDayNumber("");
    setHasSubmitted(true);

    const cleanBreakdown = (entries) =>
      entries.map((e) => (e !== "" && e !== null ? parseFloat(e) || 0 : 0));

    const payload = {
      date: date,
      practitioner: isUpdate
        ? activeReport.practitioner_id || selectedPractitioner.id
        : selectedPractitioner.id,
      practitioner_type: isUpdate
        ? activeReport.practitioner_type?.toLowerCase()
        : selectedPractitioner.practitioner_type?.toLowerCase(),
      practitioner_production: "0",
      practitioner_collection: "0",
      lab_costs: "0",
      material_costs: "0",
      invisalign_costs: "0",
      anesthesia_fee: "0",
      anesthesiologist_fee: "0",
      facility_fee: "0",
      anesthesiologist_production: "0",
      facility_production: "0",
      botox: "0",
      production_adjustment: "0",
      collection_adjustment: "0",
      hours_worked: "0",
      practitioner_production_breakdown: cleanBreakdown([]),
      practitioner_collection_breakdown: cleanBreakdown([]),
      lab_costs_breakdown: cleanBreakdown([]),
      material_costs_breakdown: cleanBreakdown([]),
      invisalign_costs_breakdown: cleanBreakdown([]),
      anesthesia_fee_breakdown: cleanBreakdown([]),
      anesthesiologist_fee_breakdown: cleanBreakdown([]),
      facility_fee_breakdown: cleanBreakdown([]),
      anesthesiologist_production_breakdown: cleanBreakdown([]),
      facility_production_breakdown: cleanBreakdown([]),
      botox_breakdown: cleanBreakdown([]),
      production_adjustment_breakdown: cleanBreakdown([]),
      collection_adjustment_breakdown: cleanBreakdown([]),
      hours_worked_breakdown: cleanBreakdown([]),
      other_profit: otherProfitTiers.map((t) => ({
        label: t.label,
        percent: Number(t.percent) || 0,
        input_value: 0,
        profit: 0,
      })),
      other_deduction: otherDeductionTiers.map((t) => buildOtherDeductionEntry(t, 0)),
      per_day_number: null,
      per_day_value: null,
      category: "performance",
    };

    submitPayload(payload);
  }

  return (
    <Card className="mt-4">
      <ConfirmModal
        isOpen={showNegativeConfirm}
        onClose={() => {
          setShowNegativeConfirm(false);
          setPendingPayload(null);
        }}
        onConfirm={handleConfirmNegative}
        title="Negative Values Detected"
        message={
          <div>
            <p className="mb-2">The following fields have negative values:</p>
            <ul className="list-disc list-inside mb-2 font-medium">
              {getNegativeFields(vals).map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <p>Are you sure this is not a mistake?</p>
          </div>
        }
        confirmText="Yes, Submit"
        cancelText="Go Back"
        confirmVariant="primary"
      />

      <ConfirmModal
        isOpen={showLabCostMismatchConfirm}
        onClose={() => {
          setShowLabCostMismatchConfirm(false);
          setPendingPayload(null);
        }}
        onConfirm={handleConfirmLabCostMismatch}
        title={
          labInvoiceSuggestions.length > 0
            ? "Lab Costs do not match PDF"
            : "Lab Costs without a PDF"
        }
        message={
          <div>
            <p className="mb-2">
              {labInvoiceSuggestions.length > 0
                ? "The entered Lab Costs do not match the assigned PDF suggested lab costs for this day."
                : "There is no lab invoice PDF backing this Lab Costs amount for this day."}
            </p>
            <div className="rounded-md bg-palette-slate-soft px-3 py-2 text-sm">
              <div>PDF Suggested Lab Costs: {formatMoney(pdfSuggestedLabCost)}</div>
              <div>Entered Lab Costs: {formatMoney(toNumber(vals.lab || 0))}</div>
            </div>
            <p className="mt-2">
              Upload the matching lab invoice PDF in the Lab Invoices tab, or adjust the Lab Costs.
              You can still submit, but this will need to be reconciled at the end of the month.
            </p>
          </div>
        }
        confirmText="Submit Anyway"
        cancelText="Go Back"
        confirmVariant="primary"
      />

      {/* Modal is view-only (canEdit false); PL still must not see fee/insurance/grid in popup — DEN72 */}
      <PractitionerQuickLookModal
        isOpen={quickLookOpen}
        onClose={() => {
          setQuickLookOpen(false);
          setActivePractitioner(null);
        }}
        practitioner={activePractitioner}
        canEdit={false}
        onSave={null}
        hideFinancialFields={isPractitionerLead(role)} // true for role 4 → same row split as directory modal
      />

      {/* The day's attachments, one panel per kind. Files are open to anyone who
          can see this page; the Lab Invoices tab is role-gated inside the panel
          because those drive costs. */}
      <DailyAttachmentsPanel
        clinicId={clinicId}
        date={date}
        role={role}
        canAssignLabInvoices={canManageLabInvoices(role)}
        selectedPractitioner={selectedPractitioner}
        selectedAttachmentId={openLabInvoiceId}
        onSelectAttachment={(id, attachment) => {
          setOpenLabInvoiceId(id);
          const pid = attachment?.practitioner_id;
          if (id && pid && rosterSorted.some((p) => p.id === pid)) {
            selectPractitioner(pid);
          }
        }}
      />

      <h2 className="text-lg font-bold mb-4">{clinicId}'s Performance Report</h2>

      <div className={`grid gap-8 ${openLabInvoiceId ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
        {/* LEFT: practitioners */}
        <Card className="shadow-none">
          {isClinicClosed && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
              <span className="text-xs font-semibold text-palette-slate-badge">
                🔒 Clinic Closed
              </span>
              <span className="text-xs text-palette-slate-placeholder">
                Scheduled closure on this date. Production, hours and collection can all still be
                entered.
              </span>
            </div>
          )}
          {isSelectedOnVacation && !isClinicClosed && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-palette-amber-border bg-palette-amber-soft px-3 py-2">
              <span className="text-xs font-semibold text-palette-amber-heading">
                {{ vacation: "✈️", sick: "🤒", personal: "🏖️", unpaid: "📋" }[selectedLeaveType] ??
                  "⏸️"}{" "}
                {{
                  vacation: "Vacation",
                  sick: "Sick Leave",
                  personal: "Personal Day",
                  unpaid: "Unpaid Leave",
                }[selectedLeaveType] ?? "Time Off"}
              </span>
              <span className="text-xs text-palette-amber-label">
                On approved leave. Production and hours can still be entered — x-rays billed under
                an absent provider and re-bills of past work both land here.
              </span>
            </div>
          )}
          <ScrollBox height={420}>
            <ListPractitioner
              practitioners={rosterSorted}
              selectedId={activePid}
              onSelect={selectPractitioner}
              reportedIds={reportedIds}
              onQuickLook={openQuickLook}
              onVacationIds={onVacationIds}
              leaveTypeByPractitionerId={leaveTypeByPractitionerId}
            />
          </ScrollBox>
        </Card>

        {/* MIDDLE: form */}
        <FormSubmit onSubmit={onSubmit} {...formTrackingProps}>
          {canEdit && isEditing && selectedPractitioner && (
            <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-palette-amber-border bg-palette-amber-soft px-4 py-2">
              <span className="text-sm text-palette-amber-text">
                Practitioner didn&apos;t work this day? Submit a zero report.
              </span>
              <Button
                type="button"
                variant="secondary"
                onClick={handleDidNotWork}
                disabled={createReport.isPending || updateReport.isPending}
              >
                Did Not Work
              </Button>
            </div>
          )}

          <CostProductionBreakdownForm
            values={vals}
            breakdowns={breakdowns}
            onBreakdownChange={handleBreakdownChange}
            errors={requiredErrors}
            showErrors={hasSubmitted}
            disabled={!isEditing}
            title="Cost & Production Breakdown for"
            practitioner={activeReport}
            hiddenColumns={hiddenColumns}
            singleInput={clinicId !== "ON-W-ERD"}
            pdfSuggestedLabCost={
              labInvoiceSuggestions.length > 0 ? pdfSuggestedLabCost.toFixed(2) : ""
            }
            pdfSuggestedLabCostComment={pdfSuggestedLabCostComment}
            pdfSuggestedLabCostWarning={labCostMismatchMessage}
            invisalignSuggestionText={invisalignSuggestionText}
            extraFieldsBeforeFees={
              clinicId === "ON-K-KIT" ? (
                <>
                  <FormField
                    label="Anesthesiologist Production"
                    type="number"
                    value={vals.anesthesiologist_production}
                    onChange={(e) =>
                      setVals((prev) => ({
                        ...prev,
                        anesthesiologist_production: e.target.value,
                      }))
                    }
                    disabled={!isEditing}
                    error={requiredErrors.anesthesiologist_production}
                    showError={hasSubmitted}
                  />
                  <FormField
                    label="Facility Production"
                    type="number"
                    value={vals.facility_production}
                    onChange={(e) =>
                      setVals((prev) => ({
                        ...prev,
                        facility_production: e.target.value,
                      }))
                    }
                    disabled={!isEditing}
                    error={requiredErrors.facility_production}
                    showError={hasSubmitted}
                  />
                </>
              ) : null
            }
          />

          {suggestionPdfs.length > 0 && (
            <div className="mt-2 rounded-md border border-palette-slate-divider bg-palette-slate-soft p-2">
              <div className="mb-1 text-xs font-medium text-palette-slate-secondary">
                Source lab invoice{suggestionPdfs.length === 1 ? "" : "s"}
              </div>
              <ul className="space-y-1">
                {suggestionPdfs.map((pdf) => (
                  <li key={pdf.attachmentId}>
                    <button
                      type="button"
                      onClick={() => setOpenLabInvoiceId(pdf.attachmentId)}
                      className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs hover:bg-palette-slate-muted ${
                        openLabInvoiceId === pdf.attachmentId
                          ? "bg-palette-slate-muted font-medium text-palette-slate-primary"
                          : "text-palette-slate-badge"
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-1">
                        <span className="text-palette-slate-placeholder">📄</span>
                        <span className="truncate">{pdf.filename}</span>
                        <span className="whitespace-nowrap text-palette-slate-placeholder">
                          · {pdf.lineCount} line{pdf.lineCount === 1 ? "" : "s"}
                        </span>
                      </span>
                      <span className="whitespace-nowrap font-medium text-palette-emerald-hover">
                        {formatMoney(pdf.amount)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasOtherProfitTiers && (
            <OtherProfitTierInputs
              tiers={otherProfitTiers}
              values={otherProfitInputs}
              onChange={setOtherProfitInputs}
              errors={otherProfitErrors}
              showErrors={hasSubmitted}
              disabled={!isEditing}
            />
          )}

          {hasOtherDeductionTiers && (
            <OtherDeductionTierInputs
              tiers={otherDeductionTiers}
              values={otherDeductionInputs}
              onChange={setOtherDeductionInputs}
              errors={otherDeductionErrors}
              showErrors={hasSubmitted}
              disabled={!isEditing}
            />
          )}

          {isPerDayEnabled && (
            <div className="mt-4 flex items-center gap-3 rounded-lg border border-palette-gray-divider bg-palette-gray-soft px-4 py-3">
              <span className="text-sm font-medium text-palette-gray-heading whitespace-nowrap">
                Day Worked
              </span>
              <div className="w-40">
                <Dropdown
                  value={perDayNumber}
                  onChange={setPerDayNumber}
                  options={PER_DAY_OPTIONS}
                  placeholder="Select"
                  disabled={!isEditing}
                />
              </div>
            </div>
          )}

          <div className="mt-5 flex justify-end gap-3">
            {!canEdit ? null : !isEditing ? (
              <Button type="button" onClick={() => setIsEditing(true)} variant="primary">
                {isUpdate ? "Edit" : "Add"}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setHasSubmitted(false);
                    resetFormToOriginal();
                  }}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit || createReport.isPending || updateReport.isPending}
                  variant="success"
                >
                  {createReport.isPending || updateReport.isPending
                    ? "Saving..."
                    : isUpdate
                      ? "Update"
                      : "Create"}
                </Button>
              </>
            )}
          </div>

          <div className="mt-2 text-xs text-palette-gray-secondary">
            Fields marked with <span className="text-palette-rose-badge">*</span> are required
          </div>
        </FormSubmit>

        {/* RIGHT: PDF preview — sticky so it stays visible while the form scrolls.
            Defaults to ~viewport height; LabInvoiceAttachmentPreview handles fit-to-width. */}
        {openLabInvoiceId && canViewLabInvoices(role) && (
          <LabInvoiceAttachmentPreview
            attachmentId={openLabInvoiceId}
            clinicId={clinicId}
            date={date}
            canAssign={canManageLabInvoices(role)}
            onClose={() => setOpenLabInvoiceId(null)}
          />
        )}
      </div>
    </Card>
  );
}
